import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import API_BASE_URL from '../config';
import Toast from '../components/common/Toast';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFormErrors, setAddressFormErrors] = useState({});
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [showCouponsList, setShowCouponsList] = useState(false);
  
  const ADVANCE_PERCENTAGE = 50;
  
  const [newAddress, setNewAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    is_default: false
  });

  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve(true);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };
    loadRazorpayScript();
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/assets/product-placeholder.jpg';
    if (imagePath.startsWith('http')) return imagePath;
    return imagePath.replace('/assets/assets/', '/assets/');
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout', message: 'Please login to checkout' } });
      return;
    }
    fetchCheckoutData();
    fetchAvailableCoupons();
  }, [isAuthenticated, navigate, user?.id]);

  const fetchAvailableCoupons = async () => {
    try {
      setCouponsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/coupons/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableCoupons(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setCouponsLoading(false);
    }
  };

  const fetchCheckoutData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [cartRes, addrRes] = await Promise.all([
        fetch(`${API_BASE_URL}/cart`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/users/${user?.id}/addresses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (cartRes.ok) {
        const d = await cartRes.json();
        setCartItems(d.data || []);
      }

      if (addrRes.ok) {
        const d = await addrRes.json();
        const addressList = d.data || [];
        setAddresses(addressList);
        
        const defaultAddr = addressList.find(a => a.is_default);
        if (defaultAddr) {
          setSelectedAddress(defaultAddr.id);
        } else if (addressList.length > 0) {
          setSelectedAddress(addressList[0].id);
        }
      }
    } catch (error) {
      showToast('Error loading checkout data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const validateAddressForm = () => {
    const errors = {};
    if (!newAddress.address_line1?.trim()) {
      errors.address_line1 = 'Address line 1 is required';
    }
    if (!newAddress.city?.trim()) {
      errors.city = 'City is required';
    }
    if (!newAddress.state?.trim()) {
      errors.state = 'State is required';
    }
    if (!newAddress.postal_code?.trim()) {
      errors.postal_code = 'Postal code is required';
    } else if (!/^\d{6}$/.test(newAddress.postal_code)) {
      errors.postal_code = 'Enter a valid 6-digit postal code';
    }
    setAddressFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    
    if (!validateAddressForm()) {
      showToast('Please fill all required fields correctly', 'error');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newAddress)
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        const newAddr = data.data;
        setAddresses(prev => [...prev, newAddr]);
        setSelectedAddress(newAddr.id);
        setShowAddressForm(false);
        setNewAddress({
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'India',
          is_default: false
        });
        setAddressFormErrors({});
        showToast('Address added successfully', 'success');
      } else {
        showToast(data.message || 'Failed to add address', 'error');
      }
    } catch (error) {
      showToast('Error adding address', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyCouponByCode = (code) => {
    setCouponCode(code);
    handleApplyCoupon(code);
  };

  const handleApplyCoupon = async (preSelectedCode = null) => {
    const codeToApply = preSelectedCode || couponCode;
    
    if (!codeToApply || !codeToApply.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    setAppliedCoupon(null);
    
    try {
      const token = localStorage.getItem('token');
      const subtotal = calculateSubtotal();
      
      const response = await fetch(`${API_BASE_URL}/orders/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          code: codeToApply.trim().toUpperCase(), 
          subtotal: subtotal
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setAppliedCoupon({
          code: data.data.code,
          discount_type: data.data.discount_type,
          discount_value: parseFloat(data.data.discount_value),
          discount_amount: parseFloat(data.data.discount_amount),
          id: data.data.id,
          description: data.data.description || ''
        });
        
        showToast(`Coupon applied! You saved ${formatPrice(data.data.discount_amount)}`, 'success');
        setCouponCode('');
        setShowCouponsList(false);
      } else {
        setCouponError(data.message || 'Invalid coupon code');
        showToast(data.message || 'Invalid coupon code', 'error');
      }
    } catch (error) {
      setCouponError('Error applying coupon');
      showToast('Error applying coupon', 'error');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    showToast('Coupon removed', 'info');
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (parseFloat(item.price) * (item.quantity || 1));
    }, 0);
  };

  const calculateDiscount = () => {
    return appliedCoupon ? parseFloat(appliedCoupon.discount_amount) : 0;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    return Math.max(0, subtotal - discount);
  };

  const calculateAdvanceAmount = () => {
    return calculateTotal() * (ADVANCE_PERCENTAGE / 100);
  };

  const calculateRemainingAmount = () => {
    return calculateTotal() - calculateAdvanceAmount();
  };

  const formatPrice = (price) => {
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const getDiscountDescription = (coupon) => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else {
      return `${formatPrice(coupon.discount_value)} OFF`;
    }
  };

  const getCouponIcon = (type) => {
    return type === 'percentage' ? '🏷️' : '💰';
  };

  const renderPrescriptionSummary = (prescription) => {
    if (!prescription) return null;
    if (prescription.type === 'image') {
      return <p className="prescription-summary">📷 Prescription: image uploaded</p>;
    }
    if (prescription.right?.sph) {
      return (
        <p className="prescription-summary">
          Prescription: R {prescription.right.sph} | L {prescription.left?.sph || '-'}
        </p>
      );
    }
    return null;
  };

  const handleHybridPayment = async (orderData) => {
    try {
      const token = localStorage.getItem('token');
      const totalAmount = calculateTotal();
      const advanceAmount = calculateAdvanceAmount();
      
      const razorpayResponse = await fetch(`${API_BASE_URL}/payment/create-razorpay-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: advanceAmount,
          full_amount: totalAmount,
          payment_percentage: ADVANCE_PERCENTAGE,
          order_data: {
            ...orderData,
            payment_method: 'half_online_half_cod'
          }
        })
      });

      const razorpayData = await razorpayResponse.json();

      if (!razorpayResponse.ok || !razorpayData.success) {
        throw new Error(razorpayData.message || 'Failed to create payment order');
      }

      const options = {
        key: 'rzp_live_SbmTpoeDljtZ5l',
        amount: razorpayData.data.amount * 100,
        currency: razorpayData.data.currency,
        name: 'Vision Care Optics',
        description: `Order ${razorpayData.data.order_number} - Pay ${ADVANCE_PERCENTAGE}% Advance, Rest on Delivery`,
        order_id: razorpayData.data.razorpay_order_id,
        handler: async (response) => {
          await completeHybridPaymentOrder(
            razorpayData.data.order_id, 
            response,
            razorpayData.data.remaining_amount
          );
        },
        prefill: {
          name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Customer',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: {
          color: '#3498db'
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            showToast('Payment cancelled', 'error');
          }
        },
        notes: {
          payment_type: 'advance',
          advance_percentage: ADVANCE_PERCENTAGE,
          remaining_amount: razorpayData.data.remaining_amount
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      showToast(error.message || 'Payment initialization failed', 'error');
      setProcessing(false);
    }
  };

  const completeHybridPaymentOrder = async (orderId, paymentResponse, remainingAmount) => {
    try {
      const token = localStorage.getItem('token');
      const finalizeResponse = await fetch(`${API_BASE_URL}/payment/finalize-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: orderId,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_signature: paymentResponse.razorpay_signature,
          remaining_amount: remainingAmount
        })
      });

      const finalizeData = await finalizeResponse.json();

      if (finalizeResponse.ok && finalizeData.success) {
        showToast(
          `Advance payment successful! Please pay ${formatPrice(remainingAmount)} on delivery.`, 
          'success'
        );
        setTimeout(() => navigate('/orders'), 3000);
      } else {
        throw new Error(finalizeData.message || 'Failed to finalize order');
      }
    } catch (error) {
      showToast(error.message || 'Payment successful but order confirmation failed. Please contact support.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      showToast('Please select a shipping address', 'error');
      return;
    }

    if (cartItems.length === 0) {
      showToast('Your cart is empty', 'error');
      return;
    }

    setProcessing(true);

    const orderData = {
      items: cartItems.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        variant_name: item.variant_name || null,
        variant_color: item.variant_color || null,
        size_name: item.size_name || null,
        size_details: item.size_details || null,
        lens_name: item.lens_name || null,
        lens_range_name: item.lens_range_name || null,
        lens_color_name: item.lens_color_name || null,
        lens_color_code: item.lens_color_code || null,
        prescription: item.prescription || null,
        quantity: item.quantity || 1,
        price: item.price,
        image: item.image || null
      })),
      address_id: selectedAddress,
      payment_method: 'half_online_half_cod',
      subtotal: calculateSubtotal(),
      coupon_code: appliedCoupon?.code || null
    };

    await handleHybridPayment(orderData);
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddress(parseInt(addressId));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (addressFormErrors[name]) {
      setAddressFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="loading-spinner">Loading checkout...</div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="empty-cart">
            <h3>Your cart is empty</h3>
            <p>Add some products to your cart to continue</p>
            <Link to="/products" className="btn-primary">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="page-title">Secure Checkout</h1>
        
        <div className="checkout-grid">
          <div className="checkout-left">
            <div className="checkout-section">
              <h2>Shipping Address</h2>
              
              {addresses.length > 0 && !showAddressForm ? (
                <div className="address-list">
                  {addresses.map(address => (
                    <label 
                      key={address.id} 
                      className={`address-option ${selectedAddress === address.id ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="address"
                        value={address.id}
                        checked={selectedAddress === address.id}
                        onChange={(e) => handleAddressSelect(e.target.value)}
                      />
                      <div className="address-details">
                        <p className="address-line">{address.address_line1}</p>
                        {address.address_line2 && <p className="address-line">{address.address_line2}</p>}
                        <p className="address-line">{address.city}, {address.state} {address.postal_code}</p>
                        <p className="address-line">{address.country}</p>
                        {address.is_default && <span className="default-badge">Default</span>}
                      </div>
                    </label>
                  ))}
                  <button 
                    className="add-address-btn" 
                    onClick={() => setShowAddressForm(true)}
                  >
                    + Add New Address
                  </button>
                </div>
              ) : !showAddressForm ? (
                <div className="no-address">
                  <p>No saved addresses. Please add a shipping address.</p>
                  <button 
                    className="add-address-btn" 
                    onClick={() => setShowAddressForm(true)}
                  >
                    + Add New Address
                  </button>
                </div>
              ) : null}

              {showAddressForm && (
                <form onSubmit={handleAddAddress} className="address-form">
                  <h3>Add New Address</h3>
                  
                  <div className="form-group">
                    <label>Address Line 1 *</label>
                    <input
                      type="text"
                      name="address_line1"
                      className={`form-input ${addressFormErrors.address_line1 ? 'error' : ''}`}
                      value={newAddress.address_line1}
                      onChange={handleInputChange}
                      placeholder="Street address, P.O. Box"
                    />
                    {addressFormErrors.address_line1 && (
                      <span className="error-message">{addressFormErrors.address_line1}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="address_line2"
                      className="form-input"
                      value={newAddress.address_line2}
                      onChange={handleInputChange}
                      placeholder="Apartment, suite, unit, etc. (Optional)"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        name="city"
                        className={`form-input ${addressFormErrors.city ? 'error' : ''}`}
                        value={newAddress.city}
                        onChange={handleInputChange}
                        placeholder="City"
                      />
                      {addressFormErrors.city && (
                        <span className="error-message">{addressFormErrors.city}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label>State *</label>
                      <input
                        type="text"
                        name="state"
                        className={`form-input ${addressFormErrors.state ? 'error' : ''}`}
                        value={newAddress.state}
                        onChange={handleInputChange}
                        placeholder="State"
                      />
                      {addressFormErrors.state && (
                        <span className="error-message">{addressFormErrors.state}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Postal Code *</label>
                      <input
                        type="text"
                        name="postal_code"
                        className={`form-input ${addressFormErrors.postal_code ? 'error' : ''}`}
                        value={newAddress.postal_code}
                        onChange={handleInputChange}
                        placeholder="6-digit PIN code"
                        maxLength="6"
                      />
                      {addressFormErrors.postal_code && (
                        <span className="error-message">{addressFormErrors.postal_code}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        className="form-input"
                        value={newAddress.country}
                        onChange={handleInputChange}
                        placeholder="Country"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={newAddress.is_default}
                        onChange={handleInputChange}
                      />
                      Set as default address
                    </label>
                  </div>
                  
                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-primary" 
                      disabled={processing}
                    >
                      {processing ? 'Saving...' : 'Save Address'}
                    </button>
                    <button 
                      type="button" 
                      className="btn-secondary" 
                      onClick={() => {
                        setShowAddressForm(false);
                        setAddressFormErrors({});
                        setNewAddress({
                          address_line1: '',
                          address_line2: '',
                          city: '',
                          state: '',
                          postal_code: '',
                          country: 'India',
                          is_default: false
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Available Coupons Section */}
            {!couponsLoading && availableCoupons.length > 0 && !appliedCoupon && (
              <div className="checkout-section available-coupons-section">
                <div className="section-header-with-toggle">
                  <h2>
                    <span className="coupon-icon">🎫</span>
                    Available Coupons
                    <span className="coupon-count-badge">{availableCoupons.length}</span>
                  </h2>
                  <button 
                    className="toggle-coupons-btn"
                    onClick={() => setShowCouponsList(!showCouponsList)}
                  >
                    {showCouponsList ? 'Hide Coupons ▲' : 'View Coupons ▼'}
                  </button>
                </div>
                
                {showCouponsList && (
                  <div className="coupons-grid">
                    {availableCoupons.map((coupon) => (
                      <div 
                        key={coupon.id} 
                        className={`coupon-card ${coupon.discount_type === 'percentage' ? 'percentage-coupon' : 'flat-coupon'}`}
                      >
                        <div className="coupon-card-left">
                          <div className="coupon-card-icon">
                            {getCouponIcon(coupon.discount_type)}
                          </div>
                        </div>
                        <div className="coupon-card-content">
                          <div className="coupon-card-header">
                            <span className="coupon-code-badge">{coupon.code}</span>
                            {coupon.min_order_value > 0 && (
                              <span className="coupon-min-order">
                                Min: {formatPrice(coupon.min_order_value)}
                              </span>
                            )}
                          </div>
                          <div className="coupon-discount-value">
                            {getDiscountDescription(coupon)}
                          </div>
                          {coupon.description && (
                            <p className="coupon-description">{coupon.description}</p>
                          )}
                          {coupon.expiry_date && (
                            <p className="coupon-expiry">
                              ⏳ Valid till: {new Date(coupon.expiry_date).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                          <button
                            className="apply-coupon-card-btn"
                            onClick={() => handleApplyCouponByCode(coupon.code)}
                            disabled={couponLoading}
                          >
                            Apply Code
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {showCouponsList && availableCoupons.length === 0 && (
                  <div className="no-coupons-message">
                    <p>🎫 No coupons available at the moment.</p>
                  </div>
                )}
              </div>
            )}

            <div className="checkout-section payment-info-section">
              <h2>Payment Information</h2>
              
              <div className="hybrid-payment-card">
                <div className="payment-type-badge">💸 50% Advance + 50% Cash on Delivery</div>
                
                <div className="payment-breakdown-details">
                  <div className="breakdown-item advance">
                    <span>Pay Now (Online):</span>
                    <strong className="advance-amount">{formatPrice(calculateAdvanceAmount())}</strong>
                    <p className="payment-note">Pay 50% securely via Razorpay (Card/UPI/NetBanking)</p>
                  </div>
                  
                  <div className="breakdown-divider">
                    <span>+</span>
                  </div>
                  
                  <div className="breakdown-item cod">
                    <span>Pay Later (On Delivery):</span>
                    <strong className="cod-amount">{formatPrice(calculateRemainingAmount())}</strong>
                    <p className="payment-note">Pay remaining 50% in cash when you receive the product</p>
                  </div>
                </div>
                
                <div className="payment-security-info">
                  <span className="security-badge">🔒 256-bit SSL Encryption</span>
                  <span className="security-badge">✅ PCI DSS Compliant</span>
                  <span className="security-badge">💳 Razorpay Secure</span>
                </div>
              </div>
              
              <div className="payment-methods-list">
                <div className="payment-method-icon">💳 Credit/Debit Card</div>
                <div className="payment-method-icon">📱 UPI</div>
                <div className="payment-method-icon">🏦 NetBanking</div>
                <div className="payment-method-icon">💼 Wallets</div>
              </div>
            </div>
          </div>

          <div className="checkout-right">
            <div className="order-summary">
              <h2>Order Summary ({cartItems.length} items)</h2>
              
              <div className="order-items">
                {cartItems.map((item, index) => (
                  <div key={item.id || `item-${index}`} className="order-item">
                    <div className="item-image">
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.product_name}
                        onError={(e) => { e.target.src = '/assets/product-placeholder.jpg'; }}
                      />
                    </div>
                    <div className="item-details">
                      <h4>{item.product_name}</h4>
                      {item.variant_name && <p>Color: {item.variant_name}</p>}
                      {item.size_name && <p>Size: {item.size_name}</p>}
                      {item.lens_name && <p>Lens: {item.lens_name}</p>}
                      {item.lens_color_name && <p>Lens Color: {item.lens_color_name}</p>}
                      {renderPrescriptionSummary(item.prescription)}
                      <p>Qty: {item.quantity || 1}</p>
                    </div>
                    <div className="item-price">
                      {formatPrice((item.price || 0) * (item.quantity || 1))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="coupon-section">
                <h3>Have a Coupon?</h3>
                <div className="coupon-input-group">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={!!appliedCoupon || couponLoading}
                  />
                  <button
                    onClick={() => handleApplyCoupon()}
                    disabled={!couponCode.trim() || !!appliedCoupon || couponLoading}
                    className="apply-coupon-btn"
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {couponError && <p className="coupon-error">{couponError}</p>}
                {appliedCoupon && (
                  <div className="applied-coupon">
                    <div className="coupon-info">
                      <span className="coupon-code">{appliedCoupon.code}</span>
                      <span className="coupon-discount">-{formatPrice(appliedCoupon.discount_amount)}</span>
                    </div>
                    <button onClick={handleRemoveCoupon} className="remove-coupon">×</button>
                  </div>
                )}
              </div>

              <div className="price-breakdown">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(calculateSubtotal())}</span>
                </div>
                {appliedCoupon && (
                  <div className="summary-row coupon-discount">
                    <span>Coupon Discount ({appliedCoupon.code})</span>
                    <span>-{formatPrice(appliedCoupon.discount_amount)}</span>
                  </div>
                )}
                <div className="summary-row shipping">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
                <div className="summary-row total">
                  <span>Total Amount</span>
                  <span>{formatPrice(calculateTotal())}</span>
                </div>
              </div>

              <div className="payment-split-breakdown">
                <div className="split-title">💰 Payment Split (50% + 50%)</div>
                <div className="split-row advance">
                  <span>Pay Now (Online):</span>
                  <strong>{formatPrice(calculateAdvanceAmount())}</strong>
                </div>
                <div className="split-row cod">
                  <span>Pay on Delivery:</span>
                  <strong>{formatPrice(calculateRemainingAmount())}</strong>
                </div>
              </div>

              <button
                className="place-order-btn"
                onClick={handlePlaceOrder}
                disabled={processing || !selectedAddress}
              >
                {processing ? (
                  <span className="processing-text">
                    <span className="spinner"></span> Processing...
                  </span>
                ) : (
                  `Pay Advance ${formatPrice(calculateAdvanceAmount())} • Rest ${formatPrice(calculateRemainingAmount())} on Delivery`
                )}
              </button>
              
              <p className="secure-checkout">
                <span className="lock-icon">🔒</span>
                Your payment information is encrypted and secure via Razorpay
              </p>
              
              <div className="razorpay-badge">
                <span>Powered by</span>
                <strong>Razorpay</strong>
              </div>
              
              <div className="cod-note">
                <p>📦 Cash on Delivery available for the remaining amount</p>
                <p className="small-note">Keep exact change ready for delivery person</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CheckoutPage;